import {
  NumberOperator,
  PropertyCondition,
  isRelatedColumn,
  isTraitColumn,
} from "src/types/visual";
import { usePredictiveTraitArtifactsQuery } from "src/graphql";
import { FilterProps } from "./condition";
import { Filter } from "./filter";
import { Column, Row, Text } from "@hightouch/htui";
import { useEffect, useMemo, useRef } from "react";
import {
  getIsChurnPredictor,
  getPredictorNumUsers,
  getQuantileData,
  isLookalikeAudience,
  quantileXKey,
  quantileYKey,
} from "src/components/traits/predictive-utils";

import { NumberRangeInputWithChart } from "./number-range-input-with-chart";

interface PredictivePropertyOperatorValueFilterProps
  extends Pick<
    FilterProps<PropertyCondition>,
    "onUpdate" | "condition" | "isDisabled"
  > {
  valueError: string | null | undefined;
}

export const PredictivePropertyOperatorValueFilter = (
  props: PredictivePropertyOperatorValueFilterProps,
) => {
  const firstNumberInputRef = useRef<HTMLInputElement | null>(null);
  const { onUpdate, condition, isDisabled } = props;

  const traitId =
    isRelatedColumn(condition.property) &&
    isTraitColumn(condition.property.column)
      ? condition.property.column.traitDefinitionId
      : "";

  const { data: trait } = usePredictiveTraitArtifactsQuery(
    {
      traitId: traitId,
      runsWhere: {
        _and: [
          {
            job_type: {
              _in: ["inference", "lookalike-inference"],
            },
          },
          { status: { _eq: "success" } },
        ],
      },
    },
    { enabled: !!traitId },
  );

  const quantiles = getQuantileData(trait);
  const numUsers = getPredictorNumUsers(trait);
  const isLookalike = useMemo(() => isLookalikeAudience(trait), [trait]);
  const isChurn = useMemo(() => getIsChurnPredictor(trait), [trait]);

  useEffect(() => {
    if (condition.operator !== NumberOperator.Between) {
      onUpdate((condition) => {
        condition.operator = NumberOperator.Between;
      });
    }
  }, [condition.operator, onUpdate]);

  useEffect(() => {
    if (condition.value == null) {
      onUpdate((condition) => {
        condition.value = { min: 80, max: 100 };
      });
    }
  });

  const handleChange = (value: { min: number; max: number }) => {
    onUpdate((condition) => {
      condition.value = value;
    });
  };

  const value = condition.value ?? { min: 80, max: 100 };

  const content =
    quantiles.length > 0 ? (
      <Column width="60vw" maxWidth="800px" minWidth="450px" p={4}>
        <NumberRangeInputWithChart
          firstNumberInputRef={firstNumberInputRef}
          value={value}
          onChange={handleChange}
          data={quantiles}
          xKey={quantileXKey}
          yKey={quantileYKey}
          totalDataCount={numUsers}
          disableTopSummary={isLookalike}
          yAxisLabel={
            isLookalike
              ? "Similarity score"
              : isChurn
                ? "Predicted likelihood to churn"
                : "Predicted likelihood to convert"
          }
          rateLabel={
            isChurn ? "predicted churn rate" : "predicted conversion rate"
          }
          tooltipLabel={
            isLookalike
              ? "Similarity score"
              : isChurn
                ? "Predicted churn rate"
                : "Predicted conversion rate"
          }
        />
      </Column>
    ) : (
      <Column p={2}>Predictor is still being trained.</Column>
    );

  return (
    <Filter
      fullWidth
      width="28"
      justifyContent="center"
      content={content}
      isDisabled={isDisabled}
      initialFocusRef={firstNumberInputRef}
    >
      <Row justifyContent="center" textAlign="center" width="100%">
        <Text variant="numeric">
          {value.min}% - {value.max}%
        </Text>
      </Row>
    </Filter>
  );
};
